import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '../AuthContext';
import { useEmulation } from './EmulationContext';
import { BrandRole, hasRolePermission, UserBrand } from '../types/brand';

export interface BrandContextType {
  currentBrandId: string | null;
  currentUserRole: BrandRole | null;
  brands: UserBrand[];
  currentBrand: UserBrand | null;
  loading: boolean;
  switchBrand: (brandId: string) => void;
  checkAccess: (requiredRole: BrandRole) => boolean;
  canManageUsers: () => boolean;
  isSuperAdmin: boolean;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};

interface BrandProviderProps {
  children: ReactNode;
}

export const BrandProvider: React.FC<BrandProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { isEmulating, emulatedUser, effectiveUser } = useEmulation();
  const [currentBrandId, setCurrentBrandId] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<BrandRole | null>(null);
  const [loading, setLoading] = useState(false);

  // Use emulated user's brands if emulating, otherwise use actual user's brands
  // Sort by created_at descending (newest first)
  const brands = (effectiveUser?.brands || []).sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return dateB - dateA; // Descending order (newest first)
  });
  const currentBrand = currentBrandId ? brands.find(b => b.id === currentBrandId) || null : null;

  // Determine if current session has SuperAdmin privileges
  // Only true SuperAdmin (not emulated) has full admin powers
  // Always returns boolean (never undefined) for type safety
  const hasRealSuperAdminPower = (): boolean => {
    if (!user) return false;
    if (!user.is_super_admin) return false;
    if (isEmulating) return false;
    return true;
  };

  // Initialize with first brand or load from localStorage
  // Reset when switching between real user and emulated user
  useEffect(() => {
    if (!effectiveUser) {
      setCurrentBrandId(null);
      setCurrentUserRole(null);
      localStorage.removeItem('current_brand_id');
      return;
    }

    // Use different storage key for emulated sessions
    const storageKey = isEmulating ? 'emulated_brand_id' : 'current_brand_id';
    const savedBrandId = localStorage.getItem(storageKey);

    const availableBrands = effectiveUser.brands || [];

    if (savedBrandId && availableBrands.some(b => b.id === savedBrandId)) {
      // User still has access to saved brand
      const brand = availableBrands.find(b => b.id === savedBrandId);
      if (brand) {
        setCurrentBrandId(savedBrandId);
        setCurrentUserRole(brand.role);
        return;
      }
    }

    // Default to first brand if available
    if (availableBrands.length > 0) {
      const firstBrand = availableBrands[0];
      setCurrentBrandId(firstBrand.id);
      setCurrentUserRole(firstBrand.role);
      localStorage.setItem(storageKey, firstBrand.id);
    } else {
      setCurrentBrandId(null);
      setCurrentUserRole(null);
      localStorage.removeItem(storageKey);
    }
  }, [effectiveUser, isEmulating]);

  const switchBrand = (brandId: string) => {
    if (!effectiveUser) {
      console.error('Cannot switch brand: user not authenticated');
      return;
    }

    // Same-brand no-op. Without this, re-selecting the current brand still
    // runs the setLoading(true→false) dance; ProtectedBrandRoute unmounts
    // its children while loading, so pages that sync the brand on mount
    // (Original, Tended) would remount-loop forever.
    if (brandId === currentBrandId) {
      return;
    }

    setLoading(true);

    const storageKey = isEmulating ? 'emulated_brand_id' : 'current_brand_id';

    // Real SuperAdmin (not emulating) can access any brand
    if (hasRealSuperAdminPower()) {
      setCurrentBrandId(brandId);
      setCurrentUserRole(BrandRole.OWNER); // SuperAdmin has full access
      localStorage.setItem(storageKey, brandId);
      setLoading(false);
      return;
    }

    // Check if effective user has access to this brand
    const brand = effectiveUser.brands?.find(b => b.id === brandId);
    if (!brand) {
      console.error('User does not have access to brand:', brandId);
      setLoading(false);
      return;
    }

    setCurrentBrandId(brandId);
    setCurrentUserRole(brand.role);
    localStorage.setItem(storageKey, brandId);
    setLoading(false);
  };

  const checkAccess = (requiredRole: BrandRole): boolean => {
    // Real SuperAdmin (not emulating) has access to everything
    if (hasRealSuperAdminPower()) {
      return true;
    }

    // No brand selected or no role
    if (!currentUserRole) {
      return false;
    }

    // Check role hierarchy based on current (possibly emulated) user's role
    return hasRolePermission(currentUserRole, requiredRole);
  };

  const canManageUsers = (): boolean => {
    // Real SuperAdmin (not emulating) can always manage users
    if (hasRealSuperAdminPower()) return true;

    // Emulated or regular users need proper role
    if (!currentUserRole) return false;
    return hasRolePermission(currentUserRole, BrandRole.ADMIN);
  };

  const value: BrandContextType = {
    currentBrandId,
    currentUserRole,
    brands,
    currentBrand,
    loading,
    switchBrand,
    checkAccess,
    canManageUsers,
    isSuperAdmin: hasRealSuperAdminPower(), // Only true if real SuperAdmin and not emulating
  };

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
};

export default BrandContext;
